import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { VaSelect } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  ariaLabels,
  POST_911_ARRAY,
  FRY_SCHOLARSHIP_ARRAY,
  SELECT_RESERVE_GI_BILL_ARRAY,
  VETERAN_READINESS_ARRAY,
  SURVIVOR_AND_DEPENDENT_ARRAY,
} from '../../constants';
import LearnMoreLabel from '../LearnMoreLabel';

const VETERAN = 'veteran';
const SPOUSE = 'spouse';
const NATIONAL_GUARD_RESERVES = 'national guard / reserves';
const WHAT_IS_YOUR_MILITARY_STATUS = "What's your military status?";
const WHAT_IS_YOUR_DEPENDENT_STATUS = "What's your dependent status?";

// Reference MONTGOMERY_GI_BILL_ARRAY in helpers file to align with initial search filter
const MONTGOMERY_GI_BILL_ARRAY = [
  { optionValue: VETERAN, optionLabel: 'Veteran' },
  { optionValue: 'active duty', optionLabel: 'Active Duty' },
  {
    optionValue: 'national guard / reserves',
    optionLabel: 'National Guard / Reserves',
  },
  { optionValue: 'spouse', optionLabel: 'Spouse' },
  { optionValue: 'child', optionLabel: 'Child' },
];

const BenefitsForm = ({
  children,
  cumulativeService,
  eligForPostGiBill,
  eligibilityChange,
  eligibilityChangeRedux,
  enlistmentService,
  giBillChapter,
  militaryStatus,
  numberOfDependents,
  showHeader,
  showModal,
  spouseActiveDuty,
  areYouActiveDuty,
}) => {
  const [whatsYourMilitaryStatusDropDown, setWhatsYourMilitaryStatusDropDown] =
    useState(POST_911_ARRAY);

  const [whatsYourStatusLabel, setWhatsYourStatusLabel] = useState(
    WHAT_IS_YOUR_MILITARY_STATUS,
  );

  const getVaSelectEvent = (event, name) => ({
    target: {
      name,
      value: event.detail?.value ?? event.target.value,
    },
  });

  const handleEligibilityChange = (event, name) => {
    eligibilityChange(getVaSelectEvent(event, name));
  };

  const preEligibilityChange = e => {
    const selectEvent = getVaSelectEvent(e, 'giBillChapter');
    const field = selectEvent.target.name;
    const { value } = selectEvent.target;

    if (field === 'giBillChapter' && value === '33a') {
      setWhatsYourMilitaryStatusDropDown(POST_911_ARRAY);
      eligibilityChangeRedux({ militaryStatus: VETERAN });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_MILITARY_STATUS);
    } else if (field === 'giBillChapter' && value === '33b') {
      setWhatsYourMilitaryStatusDropDown(FRY_SCHOLARSHIP_ARRAY);
      eligibilityChangeRedux({ militaryStatus: SPOUSE });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_DEPENDENT_STATUS);
    } else if (field === 'giBillChapter' && value === '30') {
      setWhatsYourMilitaryStatusDropDown(MONTGOMERY_GI_BILL_ARRAY);
      eligibilityChangeRedux({ militaryStatus: VETERAN });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_MILITARY_STATUS);
    } else if (field === 'giBillChapter' && value === '1606') {
      setWhatsYourMilitaryStatusDropDown(SELECT_RESERVE_GI_BILL_ARRAY);
      eligibilityChangeRedux({ militaryStatus: NATIONAL_GUARD_RESERVES });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_MILITARY_STATUS);
    } else if (field === 'giBillChapter' && value === '31') {
      setWhatsYourMilitaryStatusDropDown(VETERAN_READINESS_ARRAY);
      eligibilityChangeRedux({ militaryStatus: VETERAN });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_MILITARY_STATUS);
    } else if (field === 'giBillChapter' && value === '35') {
      // Investigate
      setWhatsYourMilitaryStatusDropDown(SURVIVOR_AND_DEPENDENT_ARRAY);
      eligibilityChangeRedux({ militaryStatus: SPOUSE });
      setWhatsYourStatusLabel(WHAT_IS_YOUR_DEPENDENT_STATUS);
    }
    eligibilityChange(selectEvent);
  };

  const cumulativeServiceOptions = () => [
    { optionValue: '1.0', optionLabel: '36+ months: 100%' }, // notice not 1.00
    { optionValue: '0.9', optionLabel: '30 months: 90%' },
    { optionValue: '0.8', optionLabel: '24 months: 80%' },
    { optionValue: '0.7', optionLabel: '18 months: 70%' },
    { optionValue: '0.6', optionLabel: '6 months: 60%' },
    { optionValue: '0.5', optionLabel: '90 days: 50%' },
    {
      optionValue: 'service discharge',
      optionLabel: 'Service-Connected Discharge: 100%',
    },
    { optionValue: 'purple heart', optionLabel: 'Purple Heart Service: 100%' },
  ];

  const renderLearnMoreLabel = ({
    text,
    modal,
    ariaLabel,
    labelFor,
    buttonId,
  }) => (
    <LearnMoreLabel
      text={text}
      onClick={() => {
        showModal(modal);
      }}
      ariaLabel={ariaLabel}
      labelFor={labelFor || modal}
      buttonId={buttonId}
    />
  );

  const renderYourMilitaryDetails = () => {
    const chapter33Check = giBillChapter === '33a' || giBillChapter === '33b';
    /*
      ***toggleCumulativeDropDown***
      Hide Cumulative Post 9/11 active-duty service drop down if applicant selects 'Fry Scholarship'
      as their GI Bill Benefit
    */
    const toggleCumulativeDropDown = () => giBillChapter !== '33b';
    return (
      <div className="vads-u-margin-bottom--3">
        <VaSelect
          label="Which GI Bill benefit do you want to use?"
          name="giBillChapter"
          value={giBillChapter}
          onVaSelect={preEligibilityChange}
        >
          <option value="33a">Post-9/11 GI Bill (Ch 33)</option>
          <option value="33b">Fry Scholarship (Ch 33)</option>
          <option value="30">Montgomery GI Bill (Ch 30)</option>
          <option value="1606">Select Reserve GI Bill (Ch 1606)</option>
          <option value="31">
            Veteran Readiness and Employment (VR&E) (Ch 31)
          </option>
          <option value="35">
            Survivors&apos; and Dependents&apos; Educational Assistance (DEA)
            (Ch 35)
          </option>
        </VaSelect>
        {renderLearnMoreLabel({
          modal: 'giBillChapter',
          ariaLabel: ariaLabels.learnMore.giBillBenefits,
          buttonId: 'gi-bill-benefits-learn-more',
        })}

        <div>
          {giBillChapter !== '' && (
            <div className="military-status-info info form-group">
              <VaSelect
                label={whatsYourStatusLabel}
                name="militaryStatus"
                value={militaryStatus}
                onVaSelect={event =>
                  handleEligibilityChange(event, 'militaryStatus')
                }
              >
                {whatsYourMilitaryStatusDropDown.map(
                  ({ optionValue, optionLabel }) => (
                    <option key={optionValue} value={optionValue}>
                      {optionLabel}
                    </option>
                  ),
                )}
              </VaSelect>

              {militaryStatus === SPOUSE && (
                <VaSelect
                  label="Is your spouse currently on active duty?"
                  name="spouseActiveDuty"
                  value={spouseActiveDuty}
                  onVaSelect={event =>
                    handleEligibilityChange(event, 'spouseActiveDuty')
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </VaSelect>
              )}

              {giBillChapter === '33b' && (
                <VaSelect
                  label="Are you currently an active duty service member?"
                  name="areYouActiveDuty"
                  value={areYouActiveDuty}
                  onVaSelect={event =>
                    handleEligibilityChange(event, 'areYouActiveDuty')
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </VaSelect>
              )}
            </div>
          )}

          {militaryStatus === 'active duty' && chapter33Check && (
            <div className="military-status-info warning form-group">
              <va-icon icon="warning" />
              <a
                title="Post 9/11 GI Bill"
                href="http://www.benefits.va.gov/gibill/post911_gibill.asp"
                id="anch_378"
                target="_blank"
                rel="noopener noreferrer"
              >
                Post 9/11 GI Bill
              </a>{' '}
              recipients serving on Active Duty (or transferee spouses of a
              service member on active duty) are not eligible to receive a
              monthly housing allowance.
            </div>
          )}
          {giBillChapter === '31' && (
            <div className="military-status-info info form-group">
              <va-icon icon="info" />
              To apply for VR&E benefits, please{' '}
              <a
                href="https://www.va.gov/careers-employment/vocational-rehabilitation/apply-vre-form-28-1900/start"
                target="_blank"
                rel="noopener noreferrer"
              >
                visit this site
              </a>
              .
            </div>
          )}
          {chapter33Check && toggleCumulativeDropDown() && (
            <>
              <VaSelect
                label="Cumulative Post-9/11 active-duty service"
                name="cumulativeService"
                value={cumulativeService}
                onVaSelect={event =>
                  handleEligibilityChange(event, 'cumulativeService')
                }
              >
                {cumulativeServiceOptions().map(
                  ({ optionValue, optionLabel }) => (
                    <option key={optionValue} value={optionValue}>
                      {optionLabel}
                    </option>
                  ),
                )}
              </VaSelect>
              {renderLearnMoreLabel({
                modal: 'cumulativeService',
                ariaLabel: ariaLabels.learnMore.post911Chapter33,
                buttonId: 'cumulative-service-learn-more',
              })}
            </>
          )}
          {giBillChapter === '30' && (
            <>
              <VaSelect
                label="Completed an enlistment of:"
                name="enlistmentService"
                value={enlistmentService}
                onVaSelect={event =>
                  handleEligibilityChange(event, 'enlistmentService')
                }
              >
                <option value="3">3+ years of enlistment</option>
                <option value="2">2 years of enlistment</option>
              </VaSelect>
              {renderLearnMoreLabel({
                modal: 'enlistmentService',
                ariaLabel: ariaLabels.learnMore.montgomeryGIBill,
                buttonId: 'enlistment-service',
              })}
            </>
          )}
          {giBillChapter === '31' && (
            <VaSelect
              label="Are you eligible for the Post-9/11 GI Bill?"
              name="eligForPostGiBill"
              value={eligForPostGiBill}
              onVaSelect={event =>
                handleEligibilityChange(event, 'eligForPostGiBill')
              }
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </VaSelect>
          )}
          {giBillChapter === '31' && eligForPostGiBill === 'no' && (
            <VaSelect
              label="How many dependents do you have?"
              name="numberOfDependents"
              value={numberOfDependents}
              onVaSelect={event =>
                handleEligibilityChange(event, 'numberOfDependents')
              }
            >
              <option value="0">0 Dependents</option>
              <option value="1">1 Dependent</option>
              <option value="2">2 Dependents</option>
              <option value="3">3 Dependents</option>
              <option value="4">4 Dependents</option>
              <option value="5">5 Dependents</option>
            </VaSelect>
          )}
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="eligibility-form">
      {showHeader && <h2 id="benefits-header">Your benefits</h2>}
      {renderYourMilitaryDetails()}
    </div>
  );
};

export default BenefitsForm;

BenefitsForm.propTypes = {
  areYouActiveDuty: PropTypes.string,
  children: PropTypes.node,
  cumulativeService: PropTypes.string,
  eligForPostGiBill: PropTypes.string,
  eligibilityChange: PropTypes.func,
  eligibilityChangeRedux: PropTypes.func,
  enlistmentService: PropTypes.string,
  giBillChapter: PropTypes.string,
  hideModal: PropTypes.func,
  militaryStatus: PropTypes.string,
  numberOfDependents: PropTypes.string,
  showHeader: PropTypes.bool,
  showModal: PropTypes.func,
  spouseActiveDuty: PropTypes.string,
  yourMilitaryDetails: PropTypes.bool,
};
